import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { Add, Edit, Delete, Info, Close } from '@mui/icons-material';
import { useHiddenExcelContext } from '../context/HiddenExcelContext';
import HiddenExcelCommand from '../../../services/command/hiddenExcel/HiddenExcelCommand.services';
import HiddenExcelQuery from '../../../services/query/hiddenExcel/HiddenExcelQuery.services';
import { SCUIButton } from '../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import Swal from 'sweetalert2';
import { HEX } from '../hiddenExcel.classes';

export const HiddenExcelDataGrid = () => {
    const { t } = useTranslation();
    const { tables, setTables, setIsLoading, setSelectedTable, setIsEditMode, setIsCreateMode } =
        useHiddenExcelContext();

    const [fieldListDialog, setFieldListDialog] = useState<{ open: boolean; fields: string[]; tableName: string }>({
        open: false,
        fields: [],
        tableName: '',
    });

    const handleEdit = (table: any) => {
        setSelectedTable(table);
        setIsEditMode(true);
        setIsCreateMode(false);
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: t('hiddenExcel.confirmDelete'),
            text: t('general.sureText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                setIsLoading(true);
                await HiddenExcelCommand.DeleteHiddenExcelTable(id);

                // Refresh the table list with selected country filter
                const selectedCountry = localStorage.getItem('selectedCountry');
                const countryId = selectedCountry ? JSON.parse(selectedCountry)?.countryId : undefined;

                const response = await HiddenExcelQuery.GetHiddenExcelTables({
                    CountryId: countryId,
                });

                if (response) {
                    setTables(response);
                }

                Swal.fire(t('hiddenExcel.tableDeleted'), '', 'success');
            } catch (error) {
                console.error('Error deleting table:', error);
                Swal.fire(t('general.error'), '', 'error');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleAddNew = () => {
        setSelectedTable(null);
        setIsCreateMode(true);
        setIsEditMode(false);
    };

    const handleViewFields = (fields: string[], tableName: string) => {
        const sortedFields = [...(fields || [])].sort((a, b) => a.localeCompare(b));
        setFieldListDialog({
            open: true,
            fields: sortedFields,
            tableName,
        });
    };

    const handleCloseFieldDialog = () => {
        setFieldListDialog({
            open: false,
            fields: [],
            tableName: '',
        });
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'name',
            headerName: t('hiddenExcel.tableName'),
            width: 200,
            flex: 1,
        },
        {
            field: 'countryId',
            headerName: t('hiddenExcel.countryId'),
            width: 120,
            type: 'number',
        },
        {
            field: 'fieldList',
            headerName: t('hiddenExcel.fieldList'),
            width: 100,
            align: 'center',
            renderCell: (params) => (
                <IconButton
                    className={HEX.inputIconViewFields}
                    size="small"
                    onClick={() => handleViewFields(params.value || [], params.row.name)}
                    sx={{
                        color: '#1565c0',
                        '&:hover': {
                            backgroundColor: '#e3f2fd',
                        },
                    }}
                >
                    <Info />
                </IconButton>
            ),
        },
        {
            field: 'actions',
            headerName: t('hiddenExcel.actions'),
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <Box className={HEX.actionsCellWrap} sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        className={HEX.inputIconEditRow}
                        size="small"
                        onClick={() => handleEdit(params.row)}
                        sx={{
                            color: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#e3f2fd',
                            },
                        }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                        className={HEX.inputIconDeleteRow}
                        size="small"
                        onClick={() => handleDelete(params.row.id)}
                        sx={{
                            color: '#d32f2f',
                            '&:hover': {
                                backgroundColor: '#ffebee',
                            },
                        }}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <>
            <div className={HEX.gridWrap} style={{ height: 600, width: '100%' }}>
                <Box className={HEX.toolbarWrap} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 2 }}>
                    <SCUIButton
                        className={HEX.inputButtonAddTable}
                        showStartIcon
                        startIcon={<Add style={{ color: 'white' }} />}
                        text={t('hiddenExcel.addTable')}
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        onClick={handleAddNew}
                    />
                </Box>
                <SCUICustomDataGrid
                    rows={tables}
                    columns={columns}
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                    disableRowSelectionOnClick
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #e0e0e0',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f5f5f5',
                            borderBottom: '2px solid #e0e0e0',
                        },
                    }}
                />
            </div>

            <Dialog
                open={fieldListDialog.open}
                onClose={handleCloseFieldDialog}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        className: HEX.fieldListDialogPaper,
                        sx: {
                            borderRadius: 2,
                        },
                    },
                }}
            >
                <DialogTitle>
                    <Box className={HEX.fieldListDialogTitleBar} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            {fieldListDialog.tableName} - {t('hiddenExcel.fieldList')}
                        </Typography>
                        <IconButton className={HEX.inputIconCloseFieldListDialog} onClick={handleCloseFieldDialog} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box className={HEX.fieldListDialogChipsWrap} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 2 }}>
                        {fieldListDialog.fields.length > 0 ? (
                            fieldListDialog.fields.map((field, index) => (
                                <Chip
                                    key={`dialog-field-${field}-${index}`}
                                    className={HEX.fieldListChip}
                                    label={field}
                                    sx={{
                                        backgroundColor: '#e3f2fd',
                                        color: '#1565c0',
                                        fontWeight: 500,
                                        border: '1px solid #90caf9',
                                    }}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {t('hiddenExcel.noFieldsFound')}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};
